/**
 * Vite-app shim for the `next/link` default export consumed by
 * `@qrody/qr-generator` (preview-panel) and `@qrody/ui` (ErrorBanner).
 *
 * Both call sites point at help-center or external URLs — they don't
 * lean on Next's prefetch or RSC integration, just the `<a>` semantics
 * with a typed `href`. A plain anchor matches the visual + behavioural
 * contract closely enough that no consumer notices the swap.
 *
 * Vite + TS aliases redirect `next/link` to this module.
 */
import type { AnchorHTMLAttributes, ReactNode } from "react";

export interface NextLinkProps
  extends Omit<AnchorHTMLAttributes<HTMLAnchorElement>, "href"> {
  href: string | { pathname: string; query?: Record<string, string> };
  prefetch?: boolean | null;
  replace?: boolean;
  scroll?: boolean;
  shallow?: boolean;
  children?: ReactNode;
}

/**
 * Plain-anchor stand-in for next/link. The Next-only props
 * (`prefetch`, `replace`, `scroll`, `shallow`) are accepted for
 * source-compat and dropped — the browser does the right thing
 * without them in a Vite SPA.
 */
export default function Link({
  href,
  prefetch: _prefetch,
  replace: _replace,
  scroll: _scroll,
  shallow: _shallow,
  children,
  ...rest
}: NextLinkProps) {
  const resolved = typeof href === "string" ? href : toUrl(href);
  return (
    <a href={resolved} {...rest}>
      {children}
    </a>
  );
}

function toUrl(href: { pathname: string; query?: Record<string, string> }): string {
  if (!href.query) return href.pathname;
  const params = new URLSearchParams(href.query);
  const search = params.toString();
  return search.length === 0 ? href.pathname : `${href.pathname}?${search}`;
}
